import type { IUser } from 'types/user'

type UsuarioDoRedirecionamento = Omit<IUser, 'organization'> & {
  organization?: IUser['organization'] | null
  skipUpload?: boolean
}

function isCertificateExpiringSoon(dateStr: string | null | undefined, daysThreshold: number = 30): boolean {
  if (!dateStr || dateStr === 'Invalid Date') return false

  const [day, month, year] = dateStr.split('/').map(Number)
  const certificateDate = new Date(year, month - 1, day)
  certificateDate.setHours(0, 0, 0, 0)

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const diffInDays = (certificateDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)

  return diffInDays <= daysThreshold
}

// why: a decisão de para onde mandar o usuário logado, fora do hook, para poder
// ser testada sem React e sem router.
export function destinoInicial(user: UsuarioDoRedirecionamento | null | undefined): string {
  if (!user) {
    return '/'
  }

  // why: administrador com várias organizações entrava no painel com token sem
  // organização, e o backend agia sobre a primeira (F1 da spec multiusuário).
  if (user.isAdmin && user.organization === null) {
    return '/select-organization'
  }

  if (user.isAdmin) {
    return '/dashboard'
  }

  if (!user.emailIsConfirmed) {
    return '/verify-email'
  }

  if (user.organization === null) {
    return '/select-organization'
  }

  if (
    user.isSeller &&
    user.organization &&
    !user.organization.hasAgreedToTermsAndConditions &&
    !user.hasAgreedToTermsAndConditions
  ) {
    return '/terms-and-conditions'
  }

  // Buyers without a valid eSocial connection (certificate or procuração)
  // must go through /datasource-connect first.
  if (
    !user.isSeller &&
    !user.skipUpload &&
    (!user.organization?.uploadedCertificate ||
      !user.organization?.certificateValidate ||
      user.organization?.certificateValidate === 'Invalid Date' ||
      isCertificateExpiringSoon(user.organization?.certificateValidate))
  ) {
    return '/datasource-connect'
  }

  // Once connected, buyers still need to select which eSocial tables to share.
  if (user.organization?.uploadedCertificate && !user.hasPermissionRequest && !user.skipUpload) {
    return '/request-permission'
  }

  return '/dashboard'
}

const ORIGEM_DE_CONFERENCIA = 'https://app.invalid'

// why: a rota privada manda ao login com `state.from` (routes/route.tsx). O destino
// só é aceito se for caminho da própria app: começa com '/', não com '//' nem '/\'
// (o navegador lê os dois como outro host), e por isso não tem esquema
// ('https:', 'javascript:'). A conferência da origem pega o que o parser de URL
// normaliza depois: '/\t/evil.com' perde o tab e vira '//evil.com'.
// hazard: o history.push do react-router (history v4) passa o caminho por
// decodeURI antes do pushState, então '/%5Cevil.com' chega como '/\evil.com'.
// Por isso a conferência vale para o texto e para ele decodificado uma vez.
export function rotaInterna(from: unknown): string | null {
  let destino: string | null = null

  if (typeof from === 'string') {
    destino = from
  } else if (from && typeof from === 'object' && typeof (from as { pathname?: unknown }).pathname === 'string') {
    const { pathname, search, hash } = from as Record<'pathname' | 'search' | 'hash', unknown>
    destino = [pathname, search, hash].filter((parte) => typeof parte === 'string').join('')
  }

  if (!destino) {
    return null
  }

  const foraDaApp = (caminho: string) =>
    !caminho.startsWith('/') ||
    caminho.startsWith('//') ||
    caminho.startsWith('/\\') ||
    new URL(caminho, ORIGEM_DE_CONFERENCIA).origin !== ORIGEM_DE_CONFERENCIA

  try {
    return [destino, decodeURI(destino)].some(foraDaApp) ? null : destino
  } catch {
    // URIError de decodeURI ('%E0%A4%A') ou URL inválida: não é destino da app.
    return null
  }
}

// why: o link profundo (ex.: e-mail ao RH da campanha) só vale para quem não tem
// etapa pendente. Termos, confirmação de e-mail, escolha de organização e eSocial
// continuam passando pelo /redirect, e aí o destino se perde como antes.
export function destinoAposLogin(user: UsuarioDoRedirecionamento | null | undefined, from: unknown): string | null {
  const destino = rotaInterna(from)

  return destino && destinoInicial(user) === '/dashboard' ? destino : null
}
